import React from 'react';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import { Home, ArrowLeft, Search, Map } from 'lucide-react';
import { Button } from './ui/Button';
import sixthLogo from '../assets/icon.svg';

const NotFoundComponent = () => {
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("jwt_token") || localStorage.getItem("web_access_token") || localStorage.getItem("vscode_jwt_token");

  const returnToMainPage = () => {
    if (isAuthenticated) {
      navigate("/vscode/dashboard");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white font-sans overflow-hidden relative flex flex-col items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ x: [0, 20, -20, 0], y: [0, -20, 20, 0], scale: [1, 1.1, 0.9, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px]"
        />
        <motion.div 
          animate={{ x: [0, -30, 20, 0], y: [0, 30, -30, 0], scale: [1, 0.9, 1.1, 1] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-600/10 rounded-full blur-[100px]"
        />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" />
      </div>

      <div className="relative z-10 text-center max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="mb-8 relative inline-block"
        >
          <h1 className="text-[12rem] font-bold leading-none bg-gradient-to-b from-white/10 to-transparent bg-clip-text text-transparent select-none">
            404
          </h1>
          <motion.div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="w-32 h-32 bg-gradient-to-br from-primary/20 to-purple-600/20 rounded-3xl flex items-center justify-center border border-primary/20 backdrop-blur-xl shadow-2xl shadow-primary/20 rotate-12">
              <img src={sixthLogo} alt="Sixth AI" className="w-16 h-16 drop-shadow-[0_0_15px_rgba(224,5,255,0.6)]" />
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-6"
        >
          <div className="space-y-2">
            <h2 className="text-3xl font-bold text-white">Page Not Found</h2>
            <p className="text-muted-foreground text-lg">
              We couldn't find the page you were looking for. It might have been removed or doesn't exist.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              onClick={returnToMainPage}
              className="bg-gradient-to-r from-[#E005FF] to-[#A73CFF] hover:opacity-90 shadow-lg shadow-primary/25 min-w-[160px]"
            >
              <Home className="w-4 h-4 mr-2" />
              {isAuthenticated ? "Go to Dashboard" : "Return Home"}
            </Button>
            
            <Button 
              variant="outline" 
              size="lg" 
              onClick={() => navigate(-1)}
              className="border-white/10 hover:bg-white/5 min-w-[160px]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
          </div>

          <div className="pt-8 flex justify-center gap-8 text-sm text-muted-foreground/60">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              <span>Check URL</span>
            </div>
            <div className="flex items-center gap-2">
              <Map className="w-4 h-4" />
              <span>Sitemap</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFoundComponent;
